import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AsyncBoundary, Card, Button, Notice, Drawer, useToast,
} from '@kollez/design-system';
import {
  useRectifications, useCreateRectification, useWithdrawRectification,
  type RectificationSubjectKind, type RectificationStatus, type CreateRectificationBody,
} from '@/lib/queries';

/**
 * /settings/privacy/rectification (DPDP §14, Page 64/N).
 *
 * A user's right to have inaccurate personal data corrected. Self-service
 * edits go through /profile PATCH (audited on their own). This surface
 * covers the harder case: rectifying data held on a fiduciary-controlled
 * record (marksheets uploaded by TPO, roster CSV imports, badge grades
 * issued by a partner). The reviewer must respond within 90 days
 * (DPDP §13(3)).
 *
 * The audit fork (Page 62/N) flagged this as LEGAL priority — API +
 * seed data existed, no page consumed it.
 */

const SUBJECT_LABEL: Record<RectificationSubjectKind, string> = {
  StudentProfile: 'My profile',
  Application: 'An application',
  Offer: 'An offer',
  Badge: 'A badge / credential',
  Membership: 'A college membership',
  Interview: 'An interview record',
  FacultyProfile: 'A faculty profile',
  Other: 'Something else',
};

const STATUS_LABEL: Record<RectificationStatus, string> = {
  pending: 'Pending',
  in_review: 'In review',
  approved: 'Approved',
  rejected: 'Rejected',
  more_info_needed: 'More info needed',
  withdrawn: 'Withdrawn',
};

function statusTone(status: RectificationStatus): 'success' | 'info' | 'warning' | 'danger' | 'default' {
  if (status === 'approved') return 'success';
  if (status === 'in_review') return 'info';
  if (status === 'rejected' || status === 'withdrawn') return 'danger';
  if (status === 'more_info_needed') return 'warning';
  return 'default';
}

function daysUntil(iso?: string): number | null {
  if (!iso) return null;
  return Math.ceil((new Date(iso).getTime() - Date.now()) / 86_400_000);
}

function truncate(v: unknown, max = 40): string {
  const s = typeof v === 'string' ? v : v === undefined || v === null ? '—' : JSON.stringify(v);
  return s.length > max ? s.slice(0, max) + '…' : s;
}

export function RectificationPage() {
  const toast = useToast();
  const list = useRectifications({ mine: true, sortBy: 'createdAt', sortDir: 'desc', limit: 50 });
  const create = useCreateRectification();

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    subjectKind: 'StudentProfile' as RectificationSubjectKind,
    subjectId: '',
    fieldPath: '',
    currentValue: '',
    proposedValue: '',
    justification: '',
  });
  const [saveError, setSaveError] = useState<string | null>(null);
  const [withdrawId, setWithdrawId] = useState<string | null>(null);
  const withdraw = useWithdrawRectification(withdrawId ?? '');

  async function submit() {
    setSaveError(null);
    if (form.fieldPath.trim().length < 2) {
      setSaveError('Which field? Example: academic.cgpa, or documents[3].url');
      return;
    }
    if (form.justification.trim().length < 20) {
      setSaveError('Please explain why the correction is needed (min 20 chars).');
      return;
    }
    if (!form.subjectId.trim()) {
      setSaveError('Which record? Copy the id from the page where the wrong value appears.');
      return;
    }
    const body: CreateRectificationBody = {
      subject: { kind: form.subjectKind, id: form.subjectId.trim() },
      fieldPath: form.fieldPath.trim(),
      currentValue: form.currentValue || undefined,
      proposedValue: form.proposedValue || '',
      justification: form.justification.trim(),
    };
    try {
      await create.mutateAsync(body);
      toast.show({ message: 'Rectification request filed. A reviewer will respond within 90 days.' });
      setOpen(false);
      setForm({
        subjectKind: 'StudentProfile', subjectId: '', fieldPath: '',
        currentValue: '', proposedValue: '', justification: '',
      });
    } catch (e) {
      setSaveError((e as Error)?.message ?? 'Could not file the request.');
    }
  }

  async function doWithdraw(id: string) {
    setWithdrawId(id);
    try {
      await withdraw.mutateAsync();
      toast.show({ message: 'Request withdrawn.' });
    } catch (e) {
      toast.show({ tone: 'warning', message: (e as Error)?.message ?? 'Could not withdraw.' });
    } finally {
      setWithdrawId(null);
    }
  }

  const rows = list.data?.rows ?? [];

  return (
    <div className="stack">
      <div className="page-header">
        <div className="ph-text">
          <p className="eyebrow">
            <Link to="/settings/privacy" className="link">← Privacy</Link>
          </p>
          <h1 className="h-page">Correct my data</h1>
          <p className="subhead">
            Under DPDP §14, you have the right to have inaccurate personal data corrected. This
            page tracks correction requests for data held on records you don't fully control
            (marksheets uploaded by your college, badges issued by a partner, roster imports).
          </p>
        </div>
        <div className="ph-actions">
          <Button variant="primary" onClick={() => setOpen(true)}>New request</Button>
        </div>
      </div>

      <Notice tone="info">
        <b>For simple edits</b> (your name, phone, resume text): go to <Link className="link" to="/profile">Profile</Link> —
        those changes save immediately.
        <br />
        <b>Use this page</b> when the wrong value lives on a record you can't directly edit — a
        recorded marksheet, an offer letter's candidate name, a badge's assigned score.
      </Notice>

      <AsyncBoundary
        kind="table"
        loading={list.isLoading}
        error={list.error}
        empty={!list.isLoading && rows.length === 0}
        emptyTitle="No rectification requests filed"
        emptyHint="If a fiduciary-held record shows wrong data about you, file a request above. A reviewer must respond within 90 days per DPDP §13(3)."
        onRetry={() => void list.refetch()}
      >
        <Card padding="md">
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Filed</th>
                  <th>Record</th>
                  <th>Field</th>
                  <th>Proposed change</th>
                  <th>Status</th>
                  <th className="r">SLA</th>
                  <th className="r"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const sla = daysUntil(r.slaDeadline);
                  const slaBreached = sla !== null && sla < 0 && r.status !== 'approved' && r.status !== 'rejected' && r.status !== 'withdrawn';
                  const canWithdraw = r.status === 'pending' || r.status === 'in_review' || r.status === 'more_info_needed';
                  return (
                    <tr key={r._id}>
                      <td className="cell-sub">
                        {new Date(r.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                      </td>
                      <td className="cell-sub">{SUBJECT_LABEL[r.subject.kind]}</td>
                      <td className="cell-sub"><code>{r.fieldPath}</code></td>
                      <td className="cell-sub">
                        {r.currentValue !== undefined && (
                          <><span className="text-ink-danger">{truncate(r.currentValue)}</span> → </>
                        )}
                        <span className="text-ink-success">{truncate(r.proposedValue)}</span>
                      </td>
                      <td>
                        <span className={`badge badge-${statusTone(r.status)}`}>
                          {STATUS_LABEL[r.status]}
                        </span>
                        {r.reviewNote && (
                          <div className="cell-sub mt-1" title={r.reviewNote}>{truncate(r.reviewNote, 60)}</div>
                        )}
                      </td>
                      <td className="r cell-sub">
                        {sla === null
                          ? '—'
                          : slaBreached
                          ? <span className="text-ink-danger" title="90-day statutory deadline breached">Breached</span>
                          : `${sla}d`}
                      </td>
                      <td className="r">
                        {canWithdraw && (
                          <button
                            type="button"
                            className="btn btn-ghost btn-sm"
                            disabled={withdrawId === r._id}
                            onClick={() => doWithdraw(r._id)}
                          >
                            Withdraw
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      </AsyncBoundary>

      <Drawer open={open} onClose={() => setOpen(false)} title="File a rectification request" size="lg">
        <div className="grid gap-4">
          {saveError && <Notice tone="warning">{saveError}</Notice>}

          <label className="grid gap-1.5">
            <span className="label">Which record is wrong?</span>
            <select
              className="select"
              value={form.subjectKind}
              onChange={(e) => setForm({ ...form, subjectKind: e.target.value as RectificationSubjectKind })}
            >
              {(Object.entries(SUBJECT_LABEL) as Array<[RectificationSubjectKind, string]>).map(([v, l]) => (
                <option key={v} value={v}>{l}</option>
              ))}
            </select>
          </label>

          <label className="grid gap-1.5">
            <span className="label">Record ID <span className="cell-sub">· copy from URL where the wrong value shows</span></span>
            <input
              className="input"
              value={form.subjectId}
              onChange={(e) => setForm({ ...form, subjectId: e.target.value })}
              placeholder="e.g. 65f2a1b7c8d4e5f6a7b8c9d0"
            />
          </label>

          <label className="grid gap-1.5">
            <span className="label">Which field?</span>
            <input
              className="input"
              value={form.fieldPath}
              onChange={(e) => setForm({ ...form, fieldPath: e.target.value })}
              placeholder="e.g. academic.cgpa, or documents[3].url"
            />
          </label>

          <div className="grid gap-3 md:grid-cols-2">
            <label className="grid gap-1.5">
              <span className="label">Current value <span className="cell-sub">· optional</span></span>
              <input
                className="input"
                value={form.currentValue}
                onChange={(e) => setForm({ ...form, currentValue: e.target.value })}
                placeholder="What it says now"
              />
            </label>
            <label className="grid gap-1.5">
              <span className="label">Proposed value</span>
              <input
                className="input"
                value={form.proposedValue}
                onChange={(e) => setForm({ ...form, proposedValue: e.target.value })}
                placeholder="What it should say"
              />
            </label>
          </div>

          <label className="grid gap-1.5">
            <span className="label">
              Why? <span className="cell-sub">· {form.justification.length}/2000 · minimum 20</span>
            </span>
            <textarea
              className="textarea"
              rows={5}
              maxLength={2000}
              value={form.justification}
              onChange={(e) => setForm({ ...form, justification: e.target.value })}
              placeholder="Give the reviewer enough context to decide. Reference the source of truth (original marksheet, official letter) if you have it."
            />
          </label>

          <Notice tone="info">
            Filing this creates a legal record. Under DPDP §13(3), the reviewer must respond within
            90 days. If the value is corrected, the change is audit-logged.
          </Notice>
        </div>

        <div className="drawer-foot">
          <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            variant="primary"
            className="ml-auto"
            loading={create.isPending}
            onClick={submit}
          >
            File request
          </Button>
        </div>
      </Drawer>
    </div>
  );
}
